import { useRef, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { importBulkXlsx } from "../lib/api";
import { formatEUR } from "../lib/format";
import { toast } from "sonner";

export default function BulkImportDialog({ open, onOpenChange, onSuccess }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | preview | importing | done
  const [report, setReport] = useState([]);

  const reset = () => { setFile(null); setPhase("idle"); setReport([]); if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
  };

  const runPreview = async () => {
    if (!file) return;
    setPhase("importing");
    try {
      const res = await importBulkXlsx(file, { dryRun: true });
      setReport(res.report || []);
      setPhase("preview");
    } catch (err) {
      toast.error("Error analizando Excel: " + (err?.response?.data?.detail || err.message));
      setPhase("idle");
    }
  };

  const runImport = async () => {
    if (!file) return;
    setPhase("importing");
    try {
      const res = await importBulkXlsx(file, { dryRun: false });
      setReport(res.report || []);
      setPhase("done");
      toast.success(`${res.imported} facturas importadas`);
      onSuccess?.();
    } catch (err) {
      toast.error("Error importando: " + (err?.response?.data?.detail || err.message));
      setPhase("preview");
    }
  };

  const allOk = report.length > 0 && report.every((r) => r.ok);
  const okCount = report.filter((r) => r.ok).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-2xl tracking-tighter">Importar histórico de facturas</DialogTitle>
          <DialogDescription>
            Sube un Excel con varias pestañas (una factura por pestaña). El sistema detecta el tipo,
            convierte importes y garantiza que el total final cuadre con el Excel.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="py-8 space-y-4">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFile}
              className="hidden"
              data-testid="bulk-file-input"
            />
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-slate-400 hover:bg-slate-50 cursor-pointer transition-colors"
              data-testid="bulk-file-dropzone"
            >
              <FileSpreadsheet size={40} className="mx-auto text-slate-400" />
              <div className="mt-3 font-medium text-slate-900">
                {file ? file.name : "Pulsa para elegir el archivo .xlsx"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {file ? `${(file.size / 1024).toFixed(0)} KB · Pulsa "Analizar" abajo` : "Una pestaña por factura"}
              </div>
            </div>
          </div>
        )}

        {phase === "importing" && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-500">
            <Loader2 size={28} className="animate-spin" />
            <div className="text-sm">Procesando...</div>
          </div>
        )}

        {(phase === "preview" || phase === "done") && (
          <div className="flex-1 overflow-auto -mx-6 px-6 py-2">
            <div className={`mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 ${
              allOk ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"
            }`}>
              {allOk ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              <span>
                <strong>{okCount}/{report.length}</strong> facturas{phase === "done" ? " importadas" : " listas"}.
                {phase === "done" && allOk && " Todos los totales coinciden al céntimo."}
                {phase === "preview" && allOk && " Pulsa \"Confirmar importación\" para guardarlas."}
                {!allOk && " Revisa las marcadas como ⚠ antes de continuar."}
              </span>
            </div>
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-[10px] font-semibold uppercase">Pestaña</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Nº Factura</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Tipo</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Fecha</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right">Total Excel</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right">Total Calc.</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center">Líneas</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.map((r) => (
                  <TableRow key={r.sheet_name} className="border-slate-100" data-testid={`bulk-row-${r.sheet_name}`}>
                    <TableCell className="font-mono text-slate-700">{r.sheet_name}</TableCell>
                    <TableCell className="font-medium text-slate-900">{r.number}</TableCell>
                    <TableCell className="text-slate-600">{r.type === "advance" ? "Adelanto" : "Final"}</TableCell>
                    <TableCell className="text-slate-600">{r.issue_date}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{formatEUR(r.expected_total || 0)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{formatEUR(r.computed_total || 0)}</TableCell>
                    <TableCell className="text-center text-slate-600">{r.lines_count}</TableCell>
                    <TableCell className="text-center">
                      {r.ok ? <span className="text-emerald-700">✓ OK</span> : <span className="text-amber-700">⚠ {r.error || "Diff"}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel-btn">Cancelar</Button>
              <Button onClick={runPreview} disabled={!file} className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-analyze-btn">
                <Upload size={14} className="mr-2" /> Analizar
              </Button>
            </>
          )}
          {phase === "preview" && (
            <>
              <Button variant="outline" onClick={reset} data-testid="bulk-back-btn">Volver a elegir</Button>
              <Button onClick={runImport} className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-confirm-btn">
                Confirmar importación ({okCount})
              </Button>
            </>
          )}
          {phase === "done" && (
            <Button onClick={() => onOpenChange(false)} className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-close-btn">Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
